/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useCallback, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Button,
    Box,
    useMultiStyleConfig,
    useBreakpointValue,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as RouteLink} from 'react-router-dom'
import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'
import {useIntl} from 'react-intl'
import {SwatchCheckIcon} from '../icons'
import useHexColor from '../../hooks/use-hex-color'

/**
 * The Swatch Component displays item inside `SwatchGroup`. For proper keyboard accessibility,
 * ensure that the rendered elements can receive keyboard focus, and are immediate siblings.
 */
const Swatch = ({
    children,
    disabled,
    href,
    label,
    name,
    selected,
    isFocusable,
    value,
    handleSelect,
    position,
    total,
    variant = 'square',
    size = 'default',
    hexColor,
    clickOnly = false,
}) => {
    const intl = useIntl()
    const isDisabled = Boolean(disabled && !href)
    const styles = useMultiStyleConfig('SwatchGroup', {
        variant,
        disabled: isDisabled,
        selected,
        size,
        hexColor,
    })
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const [selectHandlers, setSelectHandlers] = useState({})
    const {buildLocalizedUrl} = useMulticountryUtils()
    const {normalizeHexColor} = useHexColor()

    const normalizedHexColors =
        typeof hexColor === 'string'
            ? (normalizeHexColor(hexColor) || '').split(',').filter(Boolean)
            : []

    const hasHexColor = normalizedHexColors.length > 0
    const isWhiteSwatch =
        normalizedHexColors.length === 1 && /^#?(FFF|FFFFFF)$/i.test(normalizedHexColors[0])
    const isFallbackSwatch = !hasHexColor
    const colorValue = hasHexColor ? normalizedHexColors[0] : '#E8EAEF'
    const neutralForegroundCssVar = 'var(--chakra-colors-foreground-neutral-default, #13183A)'
    const multiColorBackground =
        normalizedHexColors.length > 1
            ? `linear-gradient(180deg, ${normalizedHexColors[0]} 0 50%, ${normalizedHexColors[1]} 50% 100%)`
            : undefined

    const swatchVisualStyle =
        variant === 'square' || variant === 'circle'
            ? {
                  backgroundColor: colorValue,
                  ...(multiColorBackground && {
                      backgroundImage: multiColorBackground,
                  }),
                  ...(isWhiteSwatch && {
                      border: '1px solid #13183A',
                  }),
                  ...(isFallbackSwatch && {
                      border: '1px dashed ' + neutralForegroundCssVar,
                      backgroundImage:
                          'repeating-linear-gradient(135deg, ' +
                          neutralForegroundCssVar +
                          ' 0 2px, transparent 2px 7px)',
                  }),
              }
            : undefined

    const onSelect = useCallback(
        (e) => {
            e.preventDefault()
            handleSelect(value)
        },
        [handleSelect],
    )

    useEffect(() => {
        if (!handleSelect) {
            return
        }

        setSelectHandlers({
            [!clickOnly && isDesktop ? 'onMouseEnter' : 'onClick']: onSelect,
        })
    }, [onSelect, isDesktop, clickOnly])

    return (
        <Box>
            <Button
                {...styles.swatch}
                as={href ? RouteLink : 'button'}
                to={buildLocalizedUrl(href)}
                style={swatchVisualStyle}
                aria-label={
                    value
                        ? intl.formatMessage(
                              {
                                  defaultMessage: '{name}{selected}',
                                  id: 'swatch.aria_label.color',
                              },
                              {
                                  name: name || value,
                                  selected: selected
                                      ? intl.formatMessage({
                                            defaultMessage: ' (selected)',
                                            id: 'swatch.aria_label.selected',
                                        })
                                      : '',
                              },
                          )
                        : name
                }
                aria-checked={selected}
                aria-disabled={isDisabled || undefined}
                aria-posinset={position}
                aria-setsize={total}
                id={`swatch-${value}`}
                role="radio"
                isDisabled={isDisabled}
                // To mimic the behavior of native radio inputs, only one input should be focusable.
                // (The rest are selectable via arrow keys.)
                tabIndex={isFocusable ? 0 : -1}
                bg={variant === 'square' || variant === 'circle' ? colorValue : undefined}
                {...(href ? {} : selectHandlers)}
            >
                {variant !== 'square' && variant !== 'circle' && (
                    <Box as="span" {...styles.swatchButton}>
                        {children}
                        {label && <Box as="span">{label}</Box>}
                    </Box>
                )}
                {selected && (
                    <Box as="span" {...styles.checkmarkIcon} aria-hidden="true">
                        <SwatchCheckIcon
                            aria-hidden="true"
                            {...styles.checkmarkGlyph}
                            color={
                                isWhiteSwatch || isFallbackSwatch
                                    ? '#13183A'
                                    : styles?.checkmarkGlyph?.color
                            }
                        />
                    </Box>
                )}
            </Button>
        </Box>
    )
}

Swatch.displayName = 'Swatch'

Swatch.propTypes = {
    /**
     * The children to be rendered within swatch item.
     */
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    /**
     * Indicates whether the option is disabled
     */
    disabled: PropTypes.bool,
    /**
     * Indicates whether the option is selected.
     * This props is provided internally by SwatchGroup
     */
    selected: PropTypes.bool,
    /**
     * The shape of the Swatch
     */
    variant: PropTypes.oneOf(['square', 'circle', 'image']),
    /**
     * The size of the swatch
     */
    size: PropTypes.string,
    /**
     * The label of the option.
     */
    label: PropTypes.string,
    /**
     *  The url of this option
     */
    href: PropTypes.string,
    /**
     * The display value for each swatch
     */
    name: PropTypes.string,
    /**
     * The value for the option.
     */
    value: PropTypes.string,
    /**
     * Whether the swatch can receive tab focus
     */
    isFocusable: PropTypes.bool,
    /**
     * This function is called whenever the mouse enters the swatch on desktop or when clicked on mobile.
     * The values is passed as the first argument.
     */
    handleSelect: PropTypes.func,
    /**
     * Position of this radio button in the group
     */
    position: PropTypes.number,
    /**
     * Total number of radio buttons in the group
     */
    total: PropTypes.number,
    /**
     * Hex color value for color swatches (e.g., #FF0000)
     */
    hexColor: PropTypes.string,
    /**
     * When true, selection always fires on click instead of mouseenter on desktop.
     * Use in contexts where hover-triggered changes are undesirable (e.g. compare modal).
     */
    clickOnly: PropTypes.bool,
}

export default Swatch
